import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import SimpleTooltipText from '@wix/da-ds/pkg/tooltips/SimpleTooltipText';
import { ReverseTheme } from '@wix/da-react-context/pkg/ThemeContext';
import BasicTooltip from '../../../Popover/BasicTooltip';
import { useBasicPopoverPositioning } from '@wix/da-ds/pkg/popper/hooks/useBasicPopoverPositioning';
import { usePortalNode } from '@wix/da-ds/pkg/popper/hooks/usePortalNode';
import s from './ConditionalTooltip.scss';

export interface Props {
  showTooltip: boolean;
  tooltipContent: React.ReactNode;
}
/*
  These tooltips are special snowflakes and don't use popper
  Reason being that there is a million of them, so performance is an issue;
  also they always appear in a simple fixed position so we can calculate things
  easily with just plain js and no react updates
*/
const ConditionalTooltip: React.FC<React.PropsWithChildren<Props>> = ({
  showTooltip,
  tooltipContent,
  children,
}) => {
  const portalContainer = usePortalNode('tooltip', s['tooltip-portal']);
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  const [tooltipNode, setTooltipNode] = useState<HTMLDivElement | null>(null);
  const { positionTooltip } = useBasicPopoverPositioning(contentNode, tooltipNode, {
    placement: 'right',
    enabled: showTooltip,
    updatePositionOnScroll: false,
  });

  useEffect(() => {
    if (!showTooltip || !contentNode || !tooltipNode) {
      return;
    }

    const displayTooltip = () => {
      positionTooltip();
      tooltipNode.classList.add(s['visible']);
    };
    const hideTooltip = () => {
      tooltipNode.classList.remove(s['visible']);
    };

    contentNode.addEventListener('mouseenter', displayTooltip);
    contentNode.addEventListener('mouseleave', hideTooltip);
    window.addEventListener('scroll', hideTooltip);

    return () => {
      contentNode.removeEventListener('mouseenter', displayTooltip);
      contentNode.removeEventListener('mouseleave', hideTooltip);
      window.removeEventListener('scroll', hideTooltip);
    };
  }, [contentNode, showTooltip, tooltipNode, positionTooltip]);

  if (showTooltip) {
    return (
      <div className={s['tooltip-wrapper']} ref={setContentNode}>
        {children}
        {portalContainer &&
          ReactDOM.createPortal(
            <ReverseTheme>
              <BasicTooltip placement="right" wrapperClassName={s['tooltip']} ref={setTooltipNode}>
                <SimpleTooltipText className={s['tooltip-content']}>
                  {tooltipContent}
                </SimpleTooltipText>
              </BasicTooltip>
            </ReverseTheme>,
            portalContainer
          )}
      </div>
    );
  }

  return <div className={s['tooltip-wrapper']}>{children}</div>;
};
ConditionalTooltip.displayName = 'ConditionalTooltip';

export default ConditionalTooltip;
